import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const MSG = 'İzleyici yetkisi ile dosya indirme/dışa aktarma yapılamaz';
const BLOCKED_URL = 'blob:viewer-blocked';
const warn = () => toast.error(MSG, { id: 'viewer-download-block' });

// İndirilebilir dosya MIME türleri (worker/harita blob'ları etkilenmez)
const DENY_MIME = [
  'application/pdf',
  'application/vnd',
  'application/zip',
  'application/octet-stream',
  'application/msword',
  'application/x-',
  'application/json',
  'text/csv',
  'text/html',
];

// İzleyici için uygulama GENELİNDE dosya indirme/dışa aktarma engeli.
// jsPDF (dispatchEvent), XLSX/pptxgenjs (a.click), manuel blob indirmeleri (a[download])
// ve createObjectURL üzerinden üretilen tüm dosyalar merkezi olarak kesilir.
export default function ViewerRestrictions() {
  const { user } = useAuth();
  const isViewer = !!user?.is_viewer && !user?.is_admin;

  useEffect(() => {
    if (!isViewer) return;

    const isDownloadAnchor = (a) =>
      a && a.tagName === 'A' && (a.hasAttribute('download') || (a.href && (a.href.startsWith('blob:') || a.href.startsWith('data:'))));

    // 1) Dosya blob'u üretimini kes (jsPDF, XLSX, pptxgenjs, html2pdf hepsi buradan geçer).
    //    Fırlatmak yerine sahte URL döner: gerçek blob URL hiç oluşmaz, aşağıdaki anchor yamaları indirmeyi sessizce keser.
    const origCreate = URL.createObjectURL.bind(URL);
    URL.createObjectURL = (obj) => {
      if (obj instanceof Blob && DENY_MIME.some((m) => (obj.type || '').startsWith(m))) {
        warn();
        return BLOCKED_URL;
      }
      return origCreate(obj);
    };

    // 2) Programatik indirmeler: a.click()
    const origClick = HTMLAnchorElement.prototype.click;
    HTMLAnchorElement.prototype.click = function (...args) {
      if (isDownloadAnchor(this)) {
        warn();
        return;
      }
      return origClick.apply(this, args);
    };

    // 3) Programatik indirmeler: a.dispatchEvent(new MouseEvent('click')) (FileSaver/jsPDF yolu)
    const origDispatch = HTMLAnchorElement.prototype.dispatchEvent;
    HTMLAnchorElement.prototype.dispatchEvent = function (ev) {
      if (ev && ev.type === 'click' && isDownloadAnchor(this)) {
        warn();
        return false;
      }
      return origDispatch.call(this, ev);
    };

    // 4) Kullanıcının tıkladığı indirme linkleri (capture fazında)
    const clickGuard = (e) => {
      const a = e.target && e.target.closest ? e.target.closest('a') : null;
      if (isDownloadAnchor(a)) {
        e.preventDefault();
        e.stopPropagation();
        warn();
      }
    };
    document.addEventListener('click', clickGuard, true);

    // 5) Yazdırma engeli (yazdır -> PDF kaydet yolunu kapatır)
    const origPrint = window.print;
    window.print = () => toast.error('İzleyici yetkisi ile yazdırma yapılamaz', { id: 'viewer-print-block' });

    // 6) window.open ile sahte/blob URL açılmasını kes
    const origOpen = window.open;
    window.open = function (url, ...rest) {
      const u = typeof url === 'string' ? url : (url && url.toString ? url.toString() : '');
      if (u === BLOCKED_URL || u.startsWith('blob:') || u.startsWith('data:') || /\/api\/.*\/(pdf|export|download)/.test(u)) {
        warn();
        return null;
      }
      return origOpen.call(window, url, ...rest);
    };

    return () => {
      URL.createObjectURL = origCreate;
      HTMLAnchorElement.prototype.click = origClick;
      HTMLAnchorElement.prototype.dispatchEvent = origDispatch;
      document.removeEventListener('click', clickGuard, true);
      window.print = origPrint;
      window.open = origOpen;
    };
  }, [isViewer]);

  return null;
}
